import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  CalendarCheck, ChevronRight, X, Search, Plus, Trash2, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TARGET = 480; // 8 hours

const fmtMin = (m) => {
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h === 0) return `${mins}m`;
  return mins ? `${h}h ${mins}m` : `${h}h`;
};

// ─── Progress Bar ───
const StepBar = ({ current, total }) => (
  <div className="flex gap-1.5 mb-6" data-testid="planner-progress">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
          i <= current ? 'bg-blue-600' : 'bg-zinc-200 dark:bg-zinc-700'
        }`}
      />
    ))}
  </div>
);

// ─── Step 0: Welcome ───
const WelcomeStep = ({ userName, onPlan, onSkip }) => (
  <div className="flex flex-col items-center text-center py-4" data-testid="planner-welcome">
    <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center mb-5">
      <CalendarCheck className="w-8 h-8 text-blue-600 dark:text-blue-400" />
    </div>
    <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2" data-testid="planner-greeting">
      Hi {userName}!
    </h2>
    <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
      It's another work day. Let's start by planning your day together.
    </p>
    <div className="flex items-center gap-3">
      <Button variant="outline" onClick={onSkip} data-testid="planner-skip-btn">
        Continue Without Planning
      </Button>
      <Button onClick={onPlan} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="planner-start-btn">
        Plan My Day <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

// ─── Step 1: Meetings ───
const MeetingsStep = ({ meetings, setMeetings, onBack, onNext }) => {
  const [answered, setAnswered] = useState(meetings.length > 0 ? 'yes' : null);
  const [newTitle, setNewTitle] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');

  const addMeeting = () => {
    if (!newTitle.trim()) return;
    setMeetings((prev) => [
      ...prev,
      { id: crypto.randomUUID(), title: newTitle.trim(), start_time: newStart, end_time: newEnd },
    ]);
    setNewTitle('');
    setNewStart('');
    setNewEnd('');
  };

  const removeMeeting = (id) => setMeetings((prev) => prev.filter((m) => m.id !== id));

  if (answered === null) {
    return (
      <div data-testid="planner-meetings-ask">
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-6">
          Do you have any meetings today?
        </h2>
        <div className="flex items-center justify-center gap-3 mb-8">
          <Button variant="outline" className="min-w-[80px]" onClick={() => { setAnswered('no'); }} data-testid="meetings-no-btn">
            No
          </Button>
          <Button className="min-w-[80px] bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setAnswered('yes')} data-testid="meetings-yes-btn">
            Yes
          </Button>
        </div>
        <div className="flex items-center justify-between pt-4 border-t">
          <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200" data-testid="meetings-back">
            Back
          </button>
          <Button onClick={onNext} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="meetings-next">
            Next: Tasks <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (answered === 'no') {
    return (
      <div data-testid="planner-meetings-no">
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-2">
          Do you have any meetings today?
        </h2>
        <p className="text-sm text-zinc-500 mb-4">No meetings planned — great, more focus time!</p>
        <button onClick={() => setAnswered(null)} className="text-sm text-blue-600 hover:underline mb-4 block" data-testid="meetings-change">
          Change answer
        </button>
        <div className="flex items-center justify-between pt-4 border-t">
          <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">Back</button>
          <Button onClick={onNext} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
            Next: Tasks <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // answered === 'yes'
  return (
    <div data-testid="planner-meetings-yes">
      <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-1">
        Do you have any meetings today?
      </h2>
      <p className="text-sm text-zinc-500 mb-4">
        Select the meetings you'll attend — set start & end times.
      </p>

      {/* Meeting list */}
      {meetings.length > 0 ? (
        <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
          {meetings.map((m) => (
            <div key={m.id} className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg px-3 py-2 text-sm">
              <span className="flex-1 font-medium text-zinc-800 dark:text-zinc-200 truncate">{m.title}</span>
              {m.start_time && <span className="text-zinc-500 text-xs">{m.start_time} - {m.end_time}</span>}
              <button onClick={() => removeMeeting(m.id)} className="text-red-400 hover:text-red-600">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center py-6 text-zinc-400 mb-4">
          <CalendarCheck className="h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm">No meetings added yet. Add them below.</p>
        </div>
      )}

      {/* Add meeting form */}
      <div className="flex items-end gap-2 mb-2">
        <div className="flex-1">
          <label className="text-xs font-medium text-zinc-500 block mb-1">Meeting title</label>
          <Input
            placeholder="e.g. Sprint planning"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addMeeting()}
            data-testid="meeting-title-input"
          />
        </div>
        <div className="w-24">
          <label className="text-xs font-medium text-zinc-500 block mb-1">Start</label>
          <Input type="time" value={newStart} onChange={(e) => setNewStart(e.target.value)} data-testid="meeting-start-input" />
        </div>
        <div className="w-24">
          <label className="text-xs font-medium text-zinc-500 block mb-1">End</label>
          <Input type="time" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} data-testid="meeting-end-input" />
        </div>
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={addMeeting} data-testid="meeting-add-btn">
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <button onClick={() => setAnswered(null)} className="text-sm text-blue-600 hover:underline mt-2 block" data-testid="meetings-change-yes">
        Change answer
      </button>

      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">Back</button>
        <Button onClick={onNext} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
          Next: Tasks <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── Step 2: Tasks ───
const TasksStep = ({ allTasks, selectedTasks, setSelectedTasks, onBack, onNext }) => {
  const [search, setSearch] = useState('');

  const filtered = allTasks.filter((t) =>
    t.title.toLowerCase().includes(search.toLowerCase())
  );

  const isSelected = (id) => selectedTasks.some((s) => s.id === id);

  const toggle = (task) => {
    if (isSelected(task.id)) {
      setSelectedTasks((prev) => prev.filter((s) => s.id !== task.id));
    } else {
      setSelectedTasks((prev) => [...prev, { id: task.id, title: task.title, minutes: 60, status: task.status, priority: task.priority }]);
    }
  };

  const updateMinutes = (id, min) => {
    setSelectedTasks((prev) =>
      prev.map((s) => (s.id === id ? { ...s, minutes: Math.max(0, parseInt(min, 10) || 0) } : s))
    );
  };

  const isOverdue = (t) => t.due_date && t.due_date < new Date().toISOString().slice(0, 10) && t.status !== 'Completed';

  return (
    <div data-testid="planner-tasks">
      <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-1">Your tasks</h2>
      <p className="text-sm text-zinc-500 mb-4">Select what you'll work on today and estimate the time.</p>

      {/* Search */}
      <div className="flex items-center gap-2 border rounded-lg px-3 py-2 mb-3 bg-white dark:bg-zinc-900">
        <Search className="h-4 w-4 text-zinc-400 shrink-0" />
        <input
          className="w-full text-sm bg-transparent outline-none text-zinc-800 dark:text-zinc-200 placeholder-zinc-400"
          placeholder="Search tasks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="task-search-input"
        />
      </div>

      {/* Task list */}
      <div className="max-h-56 overflow-y-auto space-y-1 mb-4" data-testid="task-list">
        {filtered.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-4">No tasks found</p>
        ) : (
          filtered.map((t) => {
            const selected = isSelected(t.id);
            const overdue = isOverdue(t);
            return (
              <div
                key={t.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                  selected ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50 border border-transparent'
                }`}
                onClick={() => toggle(t)}
                data-testid={`task-item-${t.id}`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                  selected ? 'border-blue-600 bg-blue-600' : 'border-zinc-300 dark:border-zinc-600'
                }`}>
                  {selected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
                <span className="flex-1 text-sm text-zinc-800 dark:text-zinc-200 truncate">{t.title}</span>
                {overdue && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">Overdue</span>}
                {t.priority === 'High' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400">High</span>}
                {t.priority === 'Urgent' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-600">Urgent</span>}
              </div>
            );
          })
        )}
      </div>

      {/* Time estimates for selected tasks */}
      {selectedTasks.length > 0 && (
        <div className="border-t pt-3 mb-4 space-y-2">
          <p className="text-xs font-medium text-zinc-500 mb-1">Estimate time (minutes)</p>
          {selectedTasks.map((s) => (
            <div key={s.id} className="flex items-center gap-2">
              <span className="flex-1 text-sm text-zinc-700 dark:text-zinc-300 truncate">{s.title}</span>
              <Input
                type="number"
                min={0}
                className="w-20 h-8 text-sm"
                value={s.minutes || ''}
                onChange={(e) => updateMinutes(s.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`task-minutes-${s.id}`}
              />
              <span className="text-xs text-zinc-400">min</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t">
        <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">Back</button>
        <Button onClick={onNext} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="tasks-next">
          Next: Other Work <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── Step 3: Other Work ───
const OtherWorkStep = ({ otherTasks, setOtherTasks, totalPlanned, onBack, onNext }) => {
  const remaining = TARGET - totalPlanned;

  const addTask = () => {
    setOtherTasks((prev) => [...prev, { id: crypto.randomUUID(), title: '', minutes: 0 }]);
  };

  const updateTask = (id, field, val) => {
    setOtherTasks((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: field === 'minutes' ? Math.max(0, parseInt(val, 10) || 0) : val } : t)));
  };

  const removeTask = (id) => setOtherTasks((prev) => prev.filter((t) => t.id !== id));

  return (
    <div data-testid="planner-other">
      <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-1">Any other tasks today?</h2>
      <p className="text-sm text-zinc-500 mb-4">Add anything else you plan to work on — admin, calls, planning, etc.</p>

      {/* Time tracker */}
      <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400 mb-4">
        <Clock className="h-4 w-4" />
        <span>Planned so far: <strong>{fmtMin(totalPlanned)}</strong></span>
        <span className={remaining > 0 ? 'text-amber-600' : 'text-emerald-600'}>
          ({remaining > 0 ? `${fmtMin(remaining)} remaining of ${fmtMin(TARGET)}` : 'Target reached!'})
        </span>
      </div>

      {/* Task entries */}
      <div className="space-y-2 mb-4 max-h-48 overflow-y-auto" data-testid="other-task-list">
        {otherTasks.map((t) => (
          <div key={t.id} className="flex items-center gap-2">
            <Input
              placeholder="Task description"
              value={t.title}
              onChange={(e) => updateTask(t.id, 'title', e.target.value)}
              className="flex-1 h-9 text-sm"
              data-testid={`other-task-title-${t.id}`}
            />
            <Input
              type="number"
              min={0}
              placeholder="min"
              value={t.minutes || ''}
              onChange={(e) => updateTask(t.id, 'minutes', e.target.value)}
              className="w-20 h-9 text-sm"
              data-testid={`other-task-min-${t.id}`}
            />
            <button onClick={() => removeTask(t.id)} className="text-red-400 hover:text-red-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={addTask} className="gap-2 text-sm" data-testid="other-add-btn">
        <Plus className="h-4 w-4" /> Add Task
      </Button>

      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">Back</button>
        <Button onClick={onNext} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="other-next">
          Review Plan <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── Step 4: Review ───
const ReviewStep = ({ meetings, selectedTasks, otherTasks, totalPlanned, onBack, onSave, saving }) => {
  const remaining = TARGET - totalPlanned;
  const pct = Math.min(100, Math.round((totalPlanned / TARGET) * 100));

  return (
    <div data-testid="planner-review">
      <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-1">Your Day Plan</h2>
      <p className="text-sm text-zinc-500 mb-4">Here's what you've planned for today.</p>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-zinc-600 dark:text-zinc-400">{fmtMin(totalPlanned)} planned</span>
          <span className={`font-semibold ${pct >= 100 ? 'text-emerald-600' : pct >= 75 ? 'text-blue-600' : 'text-amber-600'}`}>{pct}%</span>
        </div>
        <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-full h-2">
          <div className={`h-2 rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct >= 75 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
        </div>
        {remaining > 0 && <p className="text-xs text-amber-600 mt-1">{fmtMin(remaining)} remaining to reach 8h target</p>}
      </div>

      {/* Meetings */}
      {meetings.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Meetings ({meetings.length})</p>
          <div className="space-y-1">
            {meetings.map((m) => (
              <div key={m.id} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800/50 rounded px-3 py-1.5">
                <span className="flex-1 truncate">{m.title}</span>
                {m.start_time && <span className="text-xs text-zinc-400">{m.start_time} - {m.end_time}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks */}
      {selectedTasks.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Tasks ({selectedTasks.length})</p>
          <div className="space-y-1">
            {selectedTasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800/50 rounded px-3 py-1.5">
                <span className="flex-1 truncate">{t.title}</span>
                <span className="text-xs text-zinc-400">{fmtMin(t.minutes)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other tasks */}
      {otherTasks.filter((t) => t.title.trim()).length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Other Work</p>
          <div className="space-y-1">
            {otherTasks.filter((t) => t.title.trim()).map((t) => (
              <div key={t.id} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800/50 rounded px-3 py-1.5">
                <span className="flex-1 truncate">{t.title}</span>
                <span className="text-xs text-zinc-400">{fmtMin(t.minutes)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">Back</button>
        <Button onClick={onSave} disabled={saving} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="planner-save-btn">
          {saving ? 'Saving...' : 'Start My Day'} <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── Main DayPlannerModal ───
const DayPlannerModal = ({ onComplete, forceReplan = false }) => {
  const { user, getAuthHeaders, isSuperAdmin } = useAuth();
  const [step, setStep] = useState(forceReplan ? 1 : 0); // skip welcome on replan
  const [allTasks, setAllTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [otherTasks, setOtherTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alreadyPlanned, setAlreadyPlanned] = useState(false);

  // Check if already planned today & fetch tasks
  useEffect(() => {
    (async () => {
      try {
        const [planRes, tasksRes] = await Promise.all([
          fetch(`${API}/api/day-plans/today`, { headers: getAuthHeaders() }),
          fetch(`${API}/api/tasks/my-tasks`, { headers: getAuthHeaders() }),
        ]);
        if (planRes.ok) {
          const plan = await planRes.json();
          if (plan && !forceReplan) {
            setAlreadyPlanned(true);
            onComplete();
            return;
          }
        }
        if (tasksRes.ok) {
          const tasks = await tasksRes.json();
          setAllTasks(tasks.filter((t) => t.status !== 'Completed'));
        }
      } catch {}
      finally { setLoading(false); }
    })();
  }, [getAuthHeaders, onComplete, forceReplan]);

  const totalPlanned = (() => {
    const taskMin = selectedTasks.reduce((s, t) => s + (t.minutes || 0), 0);
    const otherMin = otherTasks.reduce((s, t) => s + (t.minutes || 0), 0);
    // Estimate meeting time from start/end
    const meetMin = meetings.reduce((s, m) => {
      if (m.start_time && m.end_time) {
        const [sh, sm] = m.start_time.split(':').map(Number);
        const [eh, em] = m.end_time.split(':').map(Number);
        return s + Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
      }
      return s;
    }, 0);
    return taskMin + otherMin + meetMin;
  })();

  const handleSkip = async () => {
    try {
      await fetch(`${API}/api/day-plans`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ skipped: true, meetings: [], tasks: [], other_tasks: [], total_planned_minutes: 0 }),
      });
    } catch {}
    onComplete();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/day-plans`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetings,
          tasks: selectedTasks.map((t) => ({ id: t.id, title: t.title, minutes: t.minutes })),
          other_tasks: otherTasks.filter((t) => t.title.trim()).map((t) => ({ title: t.title, minutes: t.minutes })),
          total_planned_minutes: totalPlanned,
          skipped: false,
        }),
      });
      toast.success('Day plan saved!');
      onComplete();
    } catch { toast.error('Failed to save plan'); }
    finally { setSaving(false); }
  };

  if (isSuperAdmin()) return null;
  if (loading || alreadyPlanned) return null;

  const firstName = (user?.full_name || '').split(' ')[0].toUpperCase();

  return (
    <div className="fixed inset-0 z-[99] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="planner-overlay">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 p-6 relative animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto" data-testid="planner-modal">
        {/* Close button */}
        {(step > 0 || forceReplan) && (
          <button
            onClick={forceReplan ? onComplete : handleSkip}
            className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
            data-testid="planner-close"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Progress bar (steps 1-4 only) */}
        {step > 0 && <StepBar current={step - 1} total={4} />}

        {/* Steps */}
        {step === 0 && !forceReplan && <WelcomeStep userName={firstName} onPlan={() => setStep(1)} onSkip={handleSkip} />}
        {step === 1 && <MeetingsStep meetings={meetings} setMeetings={setMeetings} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        {step === 2 && <TasksStep allTasks={allTasks} selectedTasks={selectedTasks} setSelectedTasks={setSelectedTasks} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        {step === 3 && <OtherWorkStep otherTasks={otherTasks} setOtherTasks={setOtherTasks} totalPlanned={totalPlanned} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
        {step === 4 && <ReviewStep meetings={meetings} selectedTasks={selectedTasks} otherTasks={otherTasks} totalPlanned={totalPlanned} onBack={() => setStep(3)} onSave={handleSave} saving={saving} />}
      </div>
    </div>
  );
};

export default DayPlannerModal;
